import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  CreateCustomFieldApplicationErrors,
  CustomField,
  GetCustomFieldResponse,
  ListCustomFieldsOptions,
  ListCustomFieldsResponse,
  UpdateCustomField,
  UpdateCustomFieldApplicationErrors,
  UpdateCustomFieldsOrderOptions,
  UpdateCustomFieldsOrderResponse,
  createCustomField as universalCreateCustomField,
  deleteCustomField as universalDeleteCustomField,
  getCustomField as universalGetCustomField,
  hideCustomField as universalHideCustomField,
  listCustomFields as universalListCustomFields,
  updateCustomField as universalUpdateCustomField,
  updateCustomFieldsOrder as universalUpdateCustomFieldsOrder,
} from './members-v1-custom-field-custom-fields.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/members' };

export function createCustomField(
  httpClient: HttpClient
): CreateCustomFieldSignature {
  return (field: NonNullablePaths<CustomField, `name`, 2>) =>
    universalCreateCustomField(
      field,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateCustomFieldSignature {
  /**
   * Creates a custom field.
   * @param - Custom field to create.
   * @returns Created custom field.
   */
  (field: NonNullablePaths<CustomField, `name`, 2>): Promise<
    NonNullablePaths<
      CustomField,
      | `defaultPrivacy`
      | `fieldType`
      | `socialType`
      | `fieldOrigin`
      | `appliesTo`
      | `section`,
      2
    > & {
      __applicationErrorsType?: CreateCustomFieldApplicationErrors;
    }
  >;
}

export function getCustomField(
  httpClient: HttpClient
): GetCustomFieldSignature {
  return (_id: string) =>
    universalGetCustomField(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface GetCustomFieldSignature {
  /**
   * Retrieves a custom field by ID.
   * @param - Custom field ID.
   */
  (_id: string): Promise<
    NonNullablePaths<
      GetCustomFieldResponse,
      | `field.defaultPrivacy`
      | `field.fieldType`
      | `field.socialType`
      | `field.fieldOrigin`
      | `field.appliesTo`
      | `field.section`,
      3
    >
  >;
}

export function listCustomFields(
  httpClient: HttpClient
): ListCustomFieldsSignature {
  return (options?: ListCustomFieldsOptions) =>
    universalListCustomFields(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListCustomFieldsSignature {
  /**
   * Retrieves a list of custom fields and fields provided by Members Area, given the provided paging.
   *
   * The fields are ordered by section in the following sequence:
   * - `GENERAL`
   * - `DISPLAY_INFO`
   * - `SOCIAL`
   *
   * The fields in the same section are ordered by the field creation date.
   *
   * To change the order of fields in sections, call Update Custom Fields Order.
   */
  (options?: ListCustomFieldsOptions): Promise<
    NonNullablePaths<
      ListCustomFieldsResponse,
      | `fields`
      | `fields.${number}.defaultPrivacy`
      | `fields.${number}.fieldType`
      | `fields.${number}.socialType`
      | `fields.${number}.fieldOrigin`
      | `fields.${number}.appliesTo`
      | `fields.${number}.section`,
      4
    >
  >;
}

export function updateCustomField(
  httpClient: HttpClient
): UpdateCustomFieldSignature {
  return (
    _id: string,
    field: NonNullablePaths<UpdateCustomField, `revision`, 2>
  ) =>
    universalUpdateCustomField(
      _id,
      field,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateCustomFieldSignature {
  /**
   * Updates a custom field.
   *
   * For custom fields you can update the following fields: `name`, `defaultPrivacy`, and `socialType`.
   *
   * For system fields you can only update the `defaultPrivacy` field.
   * @param - Custom field ID.
   * @returns Updated custom field.
   */
  (
    _id: string,
    field: NonNullablePaths<UpdateCustomField, `revision`, 2>
  ): Promise<
    NonNullablePaths<
      CustomField,
      | `defaultPrivacy`
      | `fieldType`
      | `socialType`
      | `fieldOrigin`
      | `appliesTo`
      | `section`,
      2
    > & {
      __applicationErrorsType?: UpdateCustomFieldApplicationErrors;
    }
  >;
}

export function deleteCustomField(
  httpClient: HttpClient
): DeleteCustomFieldSignature {
  return (_id: string, revision: string) =>
    universalDeleteCustomField(
      _id,
      revision,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteCustomFieldSignature {
  /**
   * Deletes a custom field and removes the corresponding column from the [Contact List](https://support.wix.com/en/article/wix-contacts-managing-your-contact-list)
   * display. The custom field is no longer suggested when adding custom fields.
   * @param - ID of the custom field to delete.
   * @param - Revision number.
   */
  (_id: string, revision: string): Promise<void>;
}

export function hideCustomField(
  httpClient: HttpClient
): HideCustomFieldSignature {
  return (_id: string, revision: string) =>
    universalHideCustomField(
      _id,
      revision,
      // @ts-ignore
      { httpClient }
    );
}

interface HideCustomFieldSignature {
  /**
   * Hides a custom field from the custom fields list, yet it's still available as a [suggested field](https://dev.wix.com/docs/rest/crm/members-contacts/members/custom-fields/custom-field-suggestions/introduction)
   * to add later.
   *
   * The related contacts custom field will still be available in the [Contact List](https://support.wix.com/en/article/wix-contacts-managing-your-contact-list).
   * @param - ID of the custom field to hide.
   * @param - Revision number.
   */
  (_id: string, revision: string): Promise<void>;
}

export function updateCustomFieldsOrder(
  httpClient: HttpClient
): UpdateCustomFieldsOrderSignature {
  return (fieldIds: string[], options?: UpdateCustomFieldsOrderOptions) =>
    universalUpdateCustomFieldsOrder(
      fieldIds,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateCustomFieldsOrderSignature {
  /**
   * Updates the order of custom fields in a section.
   * @param - IDs of the custom fields to be reordered.
   */
  (fieldIds: string[], options?: UpdateCustomFieldsOrderOptions): Promise<
    NonNullablePaths<
      UpdateCustomFieldsOrderResponse,
      | `fields`
      | `fields.${number}.defaultPrivacy`
      | `fields.${number}.fieldType`
      | `fields.${number}.socialType`
      | `fields.${number}.fieldOrigin`
      | `fields.${number}.appliesTo`
      | `fields.${number}.section`
      | `section`,
      4
    >
  >;
}

export {
  AccountInfo,
  ActionEvent,
  AppliesTo,
  Asset,
  CreateCustomFieldRequest,
  CreateCustomFieldResponse,
  CustomField,
  DefaultPrivacyChanged,
  DeleteContext,
  DeleteCustomFieldRequest,
  DeleteCustomFieldResponse,
  DeleteStatus,
  DomainEvent,
  DomainEventBodyOneOf,
  EditorlessAssigned,
  EditorlessUnassigned,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  GetCustomFieldRequest,
  GetCustomFieldResponse,
  HideCustomFieldRequest,
  HideCustomFieldResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  IncorrectFieldTypeData,
  IncorrectPrivacyData,
  InvalidSocialTypeData,
  ListCustomFieldsOptions,
  ListCustomFieldsRequest,
  ListCustomFieldsResponse,
  MessageEnvelope,
  MetaSiteSpecialEvent,
  MetaSiteSpecialEventPayloadOneOf,
  Namespace,
  NamespaceChanged,
  OdeditorAssigned,
  OdeditorUnassigned,
  Origin,
  Paging,
  PagingMetadata,
  PicassoAssigned,
  PicassoUnassigned,
  Privacy,
  ReservedFieldNameNonEditable,
  RestoreInfo,
  Section,
  ServiceProvisioned,
  ServiceRemoved,
  SiteCreated,
  SiteCreatedContext,
  SiteDeleted,
  SiteHardDeleted,
  SiteMarkedAsTemplate,
  SiteMarkedAsWixSite,
  SitePublished,
  SitePurgedExternally,
  SiteRenamed,
  SiteTransferred,
  SiteUndeleted,
  SiteUnpublished,
  SiteUrlChanged,
  SocialTypeType,
  State,
  StudioAssigned,
  StudioTwoAssigned,
  StudioTwoUnassigned,
  StudioUnassigned,
  Type,
  UpdateCustomField,
  UpdateCustomFieldRequest,
  UpdateCustomFieldResponse,
  UpdateCustomFieldsOrderOptions,
  UpdateCustomFieldsOrderRequest,
  UpdateCustomFieldsOrderResponse,
  UserDomainMediaDisabled,
  UserDomainMediaEnabled,
  WebhookIdentityType,
  WixelAssigned,
  WixelUnassigned,
} from './members-v1-custom-field-custom-fields.universal.js';
